import React, { useState, useEffect, useContext, createContext } from "react";
import PropTypes from "prop-types";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector, useDispatch } from "react-redux";
import getConfig from "config";
import { DEV_URL, rget } from "../utils/requests";
import history from "../utils/history";
import MENU_ITEMS from "../constants/menu";
import { setSelectedClientId, setSelectedClientName } from "../redux/client/actions";

const CustomAuthContext = createContext();

function CustomAuthProvider({ children }) {
  const config = getConfig();

  const DEFAULT_CLIENT_ID = config.DefaultClientId;
  // "6BD2C20F-0CE3-4B00-ACDE-63569A4273A1"; // engie enerexId
  // "BEACBE71-B7B3-EF11-8474-7C1E5210BEC2";
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  // states
  const [isAdmin, setIsAdmin] = useState(null);
  const [isSystemAdmin, setIsSystemAdmin] = useState(null);
  const [hasDatahubAccess, setHasDatahubAccess] = useState(null);
  const [isPremium, setIsPremium] = useState(null);
  const [isPremiumLoading, setIsPremiumLoading] = useState(false);
  const [premiumModuleName, setPremiumModuleName] = useState(null);
  const [moduleIsPremium, setModuleIsPremium] = useState(null);
  const [modulePremiumLoading, setModulePremiumLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [defaultClientId, setDefaultClientId] = useState(null);
  const dispatch = useDispatch();
  const clientId = useSelector((state) => state.Client.clientId);
  const clientName = useSelector((state) => state.Client.clientName);
  const [modulesAccess, setModulesAccess] = useState(new Set());
  const premiumRequestRef = React.useRef(0);

  // Wrapper to update clientId in Redux store
  const setClientId = (newClientId) => {
    dispatch(setSelectedClientId(newClientId));
  };

  const setClientName = (newClientName) => {
    dispatch(setSelectedClientName(newClientName));
  };

  useEffect(() => {
    if (user) {
      const userAppMeta =
        (user && user["https://enerex.com/app_metadata"]) || {};
      const allowedAccess = userAppMeta?.allowedAccess || [];
      const Roles = userAppMeta?.Roles || [];
      const clientIdValue = userAppMeta?.ClientId;
      setDefaultClientId(clientIdValue || DEFAULT_CLIENT_ID);

      // SystemAdmin determined by Role, not root:INS
      const isSystemAdminValue = Roles?.includes("SystemAdmin");
      const hasDatahubAccessValue = allowedAccess?.includes("access:INS");
      const isAdminValue =
        Roles?.includes("CompanyAdmin") || isSystemAdminValue;

      // read modules from nested insights.moduleAccess claim in app_metadata (or from token custom claim if present)
      const modulesFromNested = userAppMeta?.insights?.moduleAccess || [];
      const modulesFromCustomClaim =
        (user && user["https://enerex.com/insights/modules"]) || [];
      const modules =
        Array.isArray(modulesFromCustomClaim) &&
          modulesFromCustomClaim.length > 0
          ? modulesFromCustomClaim
          : Array.isArray(modulesFromNested)
            ? modulesFromNested
            : [];

      const normalizedModules = modules
        .filter((module) => typeof module === "string")
        .map((module) => module.toLowerCase());
      setModulesAccess(new Set(normalizedModules));

      // TODO: to plan on how to handle systemadmin scenario instead of default client Id
      // Only set clientId if not already set by admin dropdown
      if (!clientId) {
        setClientId(
          isSystemAdminValue && !clientIdValue ? DEFAULT_CLIENT_ID : clientIdValue
        );
      }
      setIsSystemAdmin(isSystemAdminValue);
      setIsAdmin(isAdminValue);
      setHasDatahubAccess(hasDatahubAccessValue);
    }
  }, [user]);

  useEffect(() => {
    getAccessToken();
  }, []);

  // Resolve the moduleKey for the current route from MENU_ITEMS.
  const getModuleKeyForPath = (pathname) => {
    const flatItems = MENU_ITEMS.flatMap((item) =>
      item.children ? item.children : [item]
    );
    // Find the menu item whose url best matches the current pathname.
    const match = flatItems
      .filter((item) => item.url && pathname.startsWith(item.url))
      .sort((a, b) => b.url.length - a.url.length)[0];
    return match?.moduleKey || null;
  };

  // Track current pathname so isPremium re-fetches on route change.
  const [currentPath, setCurrentPath] = useState(history.location.pathname);

  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      setCurrentPath(location.pathname);
    });
    return unlisten;
  }, []);

  const loadPremiumFlag = React.useCallback(async (moduleName = null) => {
    const resolvedModuleName = moduleName || getModuleKeyForPath(currentPath);
    const shouldCheckHeaderPremium = isSystemAdmin === true;
    const premiumClientId = clientId || defaultClientId;
    setPremiumModuleName(shouldCheckHeaderPremium ? resolvedModuleName : null);

    if (isSystemAdmin) {
      setIsPremium(true);
      setIsPremiumLoading(false);
    }

    if (isSystemAdmin === null || !isAuthenticated || !accessToken || !resolvedModuleName || !premiumClientId) {
      if (!isSystemAdmin) {
        setIsPremium(null);
        setIsPremiumLoading(false);
      }
      setModuleIsPremium(null);
      setModulePremiumLoading(false);
      return;
    }

    const requestId = premiumRequestRef.current + 1;
    premiumRequestRef.current = requestId;
    if (shouldCheckHeaderPremium) {
      setModulePremiumLoading(true);
      setModuleIsPremium(null);
    } else {
      setIsPremiumLoading(true);
      setIsPremium(null);
    }
    let resolvedPremium = null;

    try {
      const queryParams = new URLSearchParams({
        moduleName: resolvedModuleName,
      });

      queryParams.set("clientId", premiumClientId);

      const res = await rget(
        `${DEV_URL}/api/client/user/ispremium?${queryParams.toString()}`
      );
      const json = res?.data;
      if (json && typeof json.isPremium === "boolean")
        resolvedPremium = json.isPremium;
    } catch (e) {
      resolvedPremium = null;
    }

    if (premiumRequestRef.current !== requestId) return;

    if (shouldCheckHeaderPremium) {
      setModuleIsPremium(resolvedPremium === null ? true : resolvedPremium);
      setModulePremiumLoading(false);
      return;
    }

    setIsPremium(resolvedPremium === null ? false : resolvedPremium);
    setIsPremiumLoading(false);
  }, [isAuthenticated, accessToken, currentPath, isSystemAdmin, clientId, defaultClientId]);

  // Fetch premium flag whenever the route (module) changes.
  useEffect(() => {
    loadPremiumFlag();
  }, [loadPremiumFlag]);

  // const logout = () => {
  //     localStorage.removeItem('token');
  //     // Auth0logout({});
  // };

  async function getAccessToken() {
    try {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
      localStorage.setItem("token", token);
    } catch (e) {
      console.log(e);
    }
  }

  const hasModuleAccess = (moduleKey) => {
    if (!moduleKey) return true;
    if (isSystemAdmin) return true;
    return modulesAccess.has(moduleKey.toLowerCase());
  };

  return (
    <CustomAuthContext.Provider
      value={{
        logout,
        loginWithRedirect,
        isLoading,
        error,
        user,
        clientId,
        clientName,
        defaultClientId,
        setClientId: setClientId,
        setClientName: setClientName,
        isAuthenticated,
        isAdmin,
        hasDatahubAccess,
        isSystemAdmin,
        accessToken,
        isPremium,
        isPremiumLoading,
        premiumModuleName,
        moduleIsPremium,
        modulePremiumLoading,
        loadPremiumFlag,
        getAccessTokenSilently,
        hasModuleAccess,
        modules: Array.from(modulesAccess),

      }}
    >
      {children}
    </CustomAuthContext.Provider>
  );
}

const useAuth = () => useContext(CustomAuthContext);

export { useAuth };

export default CustomAuthProvider;

CustomAuthProvider.propTypes = {
  getToken: PropTypes.func,
};
